import { forwardRef, HTMLAttributes } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import combineClasses from 'classnames';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot='dialog-overlay'
      className={combineClasses('fixed inset-0 z-50 bg-slate-950/55', className)}
      {...props}
    />
  );
});

DialogOverlay.displayName = 'DialogOverlay';

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayClassName?: string;
}

const DialogContent = forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, children, overlayClassName, ...props }, ref) => {
    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Close asChild>
          <DialogOverlay className={overlayClassName} />
        </DialogPrimitive.Close>
        <DialogPrimitive.Content
          ref={ref}
          data-slot='dialog-content'
          className={combineClasses(
            'fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[min(92vw,40rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl focus:outline-none',
            className,
          )}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    );
  },
);

DialogContent.displayName = 'DialogContent';

const DialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return <div className={combineClasses('mb-4 flex flex-col gap-1.5 text-left', className)} {...props} />;
};

const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={combineClasses('font-semibold leading-tight text-slate-900', className)}
      {...props}
    />
  );
});

DialogTitle.displayName = 'DialogTitle';

const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={combineClasses('text-sm leading-relaxed text-slate-600', className)}
      {...props}
    />
  );
});

DialogDescription.displayName = 'DialogDescription';

const DialogBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return <div className={combineClasses('text-slate-800', className)} {...props} />;
};

const DialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return <div className={combineClasses('mt-6 flex items-center justify-end gap-3', className)} {...props} />;
};

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
};
